/**
 * Production error tracking (Application Insights). Disabled when no
 * connection string is configured (local dev, tests): every function is a no-op.
 *
 * Deliberately minimal: exceptions + failed API calls with their backend
 * request ID, and same-origin fetch correlation (W3C traceparent) so a
 * frontend error links to the backend's trace in App Insights. No automatic
 * page-view, click or performance collection for now.
 *
 * Identity: only the user's UUID is attached. Never send emails, names or
 * request/response bodies (PII).
 */

import { ApplicationInsights, SeverityLevel } from "@microsoft/applicationinsights-web";
import { ApiError } from "@/api/errors";
import type { RuntimeConfig } from "@/config/runtime";

let ai: ApplicationInsights | null = null;

export function initTelemetry(config: RuntimeConfig, version: string): void {
  if (ai || !config.appInsightsConnectionString) return;
  ai = new ApplicationInsights({
    config: {
      connectionString: config.appInsightsConnectionString,
      disableExceptionTracking: false, // window.onerror + unhandled rejections
      enableUnhandledPromiseRejectionTracking: true,
      enableAutoRouteTracking: false,
      autoTrackPageVisitTime: false,
      disableFetchTracking: false, // dependency + correlation headers on /api calls
      disableAjaxTracking: true,
      distributedTracingMode: 2, // W3C traceparent (matches the backend's OpenTelemetry)
      enableRequestHeaderTracking: false,
      enableResponseHeaderTracking: false,
      disableCookiesUsage: true,
    },
  });
  ai.loadAppInsights();
  ai.addTelemetryInitializer((item) => {
    item.tags = { ...item.tags, "ai.cloud.role": "web", "ai.application.ver": version };
    item.data = { ...item.data, environment: config.mode };
  });
}

/** Attach the signed-in user (UUID only) to subsequent telemetry. */
export function setTelemetryUser(userUuid: string | null): void {
  if (!ai) return;
  if (userUuid) ai.setAuthenticatedUserContext(userUuid, undefined, true);
  else ai.clearAuthenticatedUserContext();
}

/** Properties that make an error actionable: where, and which backend request. */
function errorProperties(error: unknown, context: Record<string, string>): Record<string, string> {
  const props: Record<string, string> = { ...context, path: globalThis.location?.pathname ?? "" };
  if (error instanceof ApiError) {
    props.apiStatus = String(error.status);
    if (error.code) props.apiCode = error.code;
    if (error.requestId) props.requestId = error.requestId;
  }
  return props;
}

export function reportError(error: unknown, context: Record<string, string> = {}): void {
  if (!ai) return;
  const exception = error instanceof Error ? error : new Error(String(error));
  ai.trackException({
    exception,
    severityLevel: SeverityLevel.Error,
    properties: errorProperties(error, context),
  });
}

/**
 * Should a failed request be reported? Server faults and network failures,
 * yes. Expected client outcomes (validation, permissions, not found, auth,
 * rate limits, platform blocks) are normal app flow, not bugs.
 */
export function isReportableApiError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  return error.status === 0 || error.status >= 500;
}

/** Test seam. */
export function __resetTelemetry(): void {
  ai = null;
}
